import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Upload, X, Download, CheckCircle, Plus,
  AlertTriangle, ChevronRight, ChevronLeft, FileSpreadsheet,
  ArrowRight, Info, Loader2
} from 'lucide-react';
import { getSession } from '../../../getSession';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Alert from '../../../common/Alerts/Alert';

const LOCAL_BASE_URL = 'http://localhost:62194/';
const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const IMPORT_URL = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.IMPORT_ENQIURY}`;
const Download_Template = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_ENQUIRY_COLUMNS_FOR_DOWNLOAD}`;
const GET_PREDEFINED_TEMPLATE = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.GET_PREDEFINED_TEMPLATE}`;
const Save_New_Template = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.SAVE_NEW_TEMPLATE}`;
const Get_predefined_columns = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_PREDEFINED_TEMMPLATE}`;
// ── Wizard Steps ──────────────────────────────────────────────────────────────
const STEPS = ['Select Mode', 'Upload & Map', 'Review', 'Result'];

// ── Predefined standard columns (mirrors WebForm default template) ─────────────
const STANDARD_COLUMNS = [
  'Sno', 'PersonName', 'MobileNo', 'MobileNo1', 'MobileNo2',
  'EmailID', 'EmailID1', 'EmailID2', 'CountryCode', 'CountryCode1',
  'CountryCode2', 'SourceName', 'MediumName', 'CampaignName',
  'CompanyName', 'Remarks', 'Country', 'State', 'City', 'PinCode',
  'ResiAddress', 'OfficeAddress', 'AssignTo', 'EnquiryTags'
];

// ── Helper ────────────────────────────────────────────────────────────────────
function readExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

        // Extract headers explicitly
        const headersJson = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const headers = headersJson.length > 0 ? headersJson[0] : [];

        resolve({ data: json, headers: headers });
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function EnquiryImport({ onClose, onSuccess }) {
  const { userId, TokenId, parentId } = getSession();
  const TOKEN = TokenId || '-2295521862261168';

  // wizard
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('bulk_import'); // 'bulk_import' | 'bulk_update'

  // file & parsed data
  const [file, setFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [uploadedColumns, setUploadedColumns] = useState([]);
  const fileInputRef = useRef();

  // mapping
  const [templateName, setTemplateName] = useState('');
  const [columnMap, setColumnMap] = useState({}); // { StandardCol: uploadedCol | '__other__:value' }
  const [mappingTypes, setMappingTypes] = useState({}); // { StandardCol: 'file' | 'other' }

  // templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('0'); // 0 = PredefinedField

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const payload = {
          Token: TOKEN,
          Details: { ParentId: Number(parentId) || 0 }
        };
        const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_PREDEFINED_TEMPLATE}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const json = await response.json();
        if (json.Status === 1 && Array.isArray(json.Details)) {
          setTemplates(json.Details);
        }
      } catch (e) {
        console.error("Failed to fetch templates", e);
      }
    };
    fetchTemplates();
  }, [TOKEN, parentId]);

  // options
  const [autoDistribute, setAutoDistribute] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  // status
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [alertConfig, setAlertConfig] = useState({ show: false, type: 'success', title: '', message: '', onConfirm: null });

  // drag
  const [isDragging, setIsDragging] = useState(false);

  // ── File handling ─────────────────────────────────────────────────────────
  const processFile = useCallback(async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toUpperCase();
    if (!['XLS', 'XLSX'].includes(ext)) {
      setError('Please upload only .xls or .xlsx files.');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const { data, headers } = await readExcelFile(f);
      if (headers.length === 0) { setError('No columns found in file.'); return; }

      // Allow 0 data rows ONLY if defining a new template
      if (data.length === 0 && selectedTemplateId !== 'new') {
        setError('No rows found in file. Data is required for import.');
        return;
      }

      // Column Validation Logic
      if (selectedTemplateId !== 'new') {
        let expectedHeaders = [];
        let hasErrors = false;

        if (selectedTemplateId === '0') {
          // Fetch predefined template blob from DB
          const payload = {
            Token: TOKEN,
            Details: {
              ParentId: Number(parentId) || 0,
              SelectedValue: 0,
              TemplateName: "Enquiry"
            }
          };
          const response = await fetch(Get_predefined_columns, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (response.ok) {
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const wb = XLSX.read(arrayBuffer, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const headersJson = XLSX.utils.sheet_to_json(ws, { header: 1 });
            if (headersJson.length > 0) {
              expectedHeaders = headersJson[0];
            }
          } else {
            setError("Failed to fetch predefined template columns for validation.");
            return;
          }
        } else {
          // Fetch saved template mapping from DB
          const payload = {
            Token: TOKEN,
            Details: JSON.stringify({ MappingId: Number(selectedTemplateId) })
          };
          const response = await fetch(Download_Template, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const json = await response.json();
          if (json.Status === 1 && json.Details && json.Details.length > 0) {
            let mappingDetailsStr = json.Details[0].MappingDetails;
            if (mappingDetailsStr) {
              const mappingObj = JSON.parse(mappingDetailsStr);
              expectedHeaders = Object.keys(mappingObj);
            }
          }
        }

        if (expectedHeaders.length > 0) {
          if (headers.length !== expectedHeaders.length) {
            hasErrors = true;
          } else {
            const expectedLower = expectedHeaders.map(h => String(h).trim().toLowerCase());
            for (let i = 0; i < headers.length; i++) {
              if (!expectedLower.includes(String(headers[i]).trim().toLowerCase())) {
                hasErrors = true;
                break;
              }
            }
          }

          if (hasErrors) {
            setError("Upload field count does not match the import field count, or the uploaded Excel does not match the selected import template. Please download the template again, fill it correctly, and try importing.");
            setFile(null);
            return;
          }
        }
      }

      if (data.length > 10000) { setError('Maximum 10,000 records allowed per upload.'); return; }
      
      setFile(f);
      setImportData(data);
      setUploadedColumns(headers);

      // auto-map columns that match standard names
      const autoMap = {};
      const autoTypes = {};
      headers.forEach(col => {
        if (!col) return;
        const match = STANDARD_COLUMNS.find(s => s.toLowerCase() === String(col).trim().toLowerCase());
        if (match) {
          autoMap[match] = col;
          autoTypes[match] = 'file';
        }
      });
      setColumnMap(autoMap);
      setMappingTypes(autoTypes);
    } catch (e) {
      console.error(e);
      setError('Failed to process file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedTemplateId, TOKEN, parentId]);

  const handleDownloadTemplate = async () => {
    if (selectedTemplateId === '0' || selectedTemplateId === 'new') {
      downloadSample();
      return;
    }
    try {
      const payload = {
        Token: TOKEN,
        Details: JSON.stringify({ MappingId: Number(selectedTemplateId) })
      };
      const response = await fetch(Download_Template, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await response.json();
      if (json.Status === 1 && json.Details && json.Details.length > 0) {
        let mappingDetailsStr = json.Details[0].MappingDetails;
        if (mappingDetailsStr) {
          const mappingObj = JSON.parse(mappingDetailsStr);
          const headers = Object.keys(mappingObj);
          const ws = XLSX.utils.aoa_to_sheet([headers]);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Template');
          XLSX.writeFile(wb, 'EnquiryImportTemplate.xlsx');
        } else {
          downloadSample();
        }
      } else {
        downloadSample();
      }
    } catch (e) {
      console.error("Failed to download template", e);
      downloadSample();
    }
  };

  const handleSaveMapping = async () => {
    if (!templateName.trim()) {
      setError("Please enter a Template Name before saving.");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const MapFields = {};
      Object.entries(columnMap).forEach(([stdCol, uploadedCol]) => {
        if (uploadedCol && uploadedCol.startsWith('__other__:')) {
          MapFields[stdCol] = `${uploadedCol.replace('__other__:', '')} (Other Field)`;
        } else if (uploadedCol) {
          MapFields[stdCol] = uploadedCol;
        }
      });

      const OriginalFields = JSON.stringify(uploadedColumns.map(c => ({ name: c })));

      const payload = {
        Token: TOKEN,
        Details: JSON.stringify({
          ParentId: Number(parentId) || 0,
          TemplateName: templateName.trim(),
          MapFields: MapFields,
          OriginalFields: OriginalFields,
          CreatedBy: Number(userId) || 0
        })
      };

      const response = await fetch(Save_New_Template, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await response.json();
      if (response.ok && json.Status === 1) {
        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Template Saved!',
          message: 'Your custom import template has been saved successfully.',
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
      } else {
        setError(json.Message || 'Failed to save template.');
      }
    } catch (e) {
      console.error(e);
      setError('Network error. Failed to save template.');
    } finally {
      setIsLoading(false);
    }
  };

  const onFileDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  // ── Build final payload & submit ──────────────────────────────────────────
  const handleSubmit = async () => {
    setIsLoading(true); setError('');
    try {
      // Apply column mapping to data
      let finalData = importData;
      if (selectedTemplateId === 'new' && Object.keys(columnMap).length > 0) {
        finalData = importData.map(row => {
          const mapped = {};

          // First, add all columns that are NOT mapped to a standard column
          // so custom fields (like "amount", "Service") are retained in the JSON.
          // Filter out '__EMPTY' keys generated by xlsx for columns without headers.
          Object.keys(row).forEach(excelCol => {
            const isMappedToStandard = Object.values(columnMap).includes(excelCol);
            if (!isMappedToStandard && !excelCol.startsWith('__EMPTY')) {
              mapped[excelCol] = row[excelCol] !== undefined ? String(row[excelCol]) : null;
            }
          });

          // Second, add standard columns based on mapping
          STANDARD_COLUMNS.forEach(stdCol => {
            const src = columnMap[stdCol];
            if (!src) return; // DO NOT set null for unmapped standard columns

            if (src.startsWith('__other__:')) {
              mapped[stdCol] = src.replace('__other__:', '');
            } else {
              mapped[stdCol] = row[src] !== undefined ? String(row[src]) : null;
            }
          });
          return mapped;
        });
      }

      const payload = {
        Token: TOKEN,
        Details: {
          ImportData: finalData,
          UserId: Number(userId) || 0,
          FileName: file?.name || '',
          IsUpdate: isUpdate,
          AutoLeadDistribution: autoDistribute,
          MappingId: selectedTemplateId === 'new' ? 0 : (Number(selectedTemplateId) || 0),
        }
      };

      const response = await fetch(IMPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await response.json();
      if (response.ok && json.Status === 1) {
        const importId = json.Details?.[0]?.Column1 || json.Details?.[0]?.[Object.keys(json.Details[0])[0]] || '';
        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Import Successful!',
          message: `Import ID: ${importId} | ${importData.length.toLocaleString()} records queued for processing. The list will refresh shortly.`,
          onConfirm: () => {
            setAlertConfig(prev => ({ ...prev, show: false }));
            // Delay refresh so background job has time to process
            if (onSuccess) setTimeout(() => onSuccess(), 2000);
            else onClose();
          }
        });
      } else {
        setAlertConfig({
          show: true,
          type: 'error',
          title: 'Import Failed',
          message: json.Message || 'An error occurred. Please try again or contact support.',
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
      }
    } catch (e) {
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Network Error',
        message: 'Could not reach the server. Please check your connection and try again.',
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Download sample template ───────────────────────────────────────────────
  // const downloadSample = () => {
  //   const ws = XLSX.utils.aoa_to_sheet([STANDARD_COLUMNS]);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, 'Template');
  //   XLSX.writeFile(wb, 'EnquiryImportTemplate.xlsx');
  // };

  const downloadSample = async () => {
    try {
      const payload = {
        Token: TOKEN,
        Details: {
          ParentId: Number(parentId) || 0,
          SelectedValue: 0,
          TemplateName: "Enquiry"
        }
      };
      
      const response = await fetch(Get_predefined_columns, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to fetch predefined template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'EnquiryImportTemplate.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (e) {
      console.error("Failed to download predefined template", e);
      setError("Failed to download template. Please try again.");
    }
  };

  // ── Step renders ──────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <div style={{ padding: '32px 24px' }}>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Choose how you want to process your Excel file:
      </p>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[
          { key: 'bulk_import', label: 'Bulk Import', desc: 'Add new enquiry records from Excel. Max 10,000 rows.', icon: '📥' },
          { key: 'bulk_update', label: 'Bulk Update', desc: 'Update existing enquiries using EnquiryID. Max 100 rows.', icon: '🔄' }
        ].map(opt => (
          <div
            key={opt.key}
            onClick={() => { setMode(opt.key); setIsUpdate(opt.key === 'bulk_update'); }}
            style={{
              flex: 1, padding: '20px 18px', borderRadius: 12, cursor: 'pointer',
              border: `2px solid ${mode === opt.key ? '#3b82f6' : '#e2e8f0'}`,
              background: mode === opt.key ? '#eff6ff' : '#f8fafc',
              transition: 'all .2s'
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 10 }}>{opt.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 6 }}>{opt.label}</div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{opt.desc}</div>
          </div>
        ))}
      </div>

      {/* Template Selection */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Import Template:</label>
        <div style={{ display: 'flex', gap: 10 }}>
          {selectedTemplateId === 'new' ? (
            <input
              type="text"
              placeholder="Enter Template name"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, outline: 'none' }}
            />
          ) : (
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, outline: 'none' }}
            >
              <option value="0">PredefinedField</option>
              {templates.map(t => (
                <option key={t.MappingId} value={t.MappingId}>{t.MappingName}</option>
              ))}
            </select>
          )}

          <button
            onClick={() => {
              if (selectedTemplateId === 'new') setSelectedTemplateId('0');
              else setSelectedTemplateId('new');
            }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: 8, border: '1px solid #10b981', background: '#f0fdf4', color: '#10b981', fontWeight: 600, cursor: 'pointer' }}
            title={selectedTemplateId === 'new' ? "Cancel New Template" : "Add New Template"}
          >
            {selectedTemplateId === 'new' ? <X size={18} /> : <Plus size={18} />}
          </button>

          <button
            onClick={handleDownloadTemplate}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: 8, border: '1px solid #3b82f6', background: 'white', color: '#3b82f6', fontWeight: 600, cursor: 'pointer' }}
            title="Download Template"
          >
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onFileDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragging ? '#3b82f6' : file ? '#10b981' : '#cbd5e1'}`,
          borderRadius: 12, padding: '36px 24px', textAlign: 'center',
          background: isDragging ? '#eff6ff' : file ? '#f0fdf4' : '#f8fafc',
          cursor: 'pointer', transition: 'all .2s', marginBottom: 16
        }}
      >
        <input ref={fileInputRef} type="file" accept=".xls,.xlsx" hidden onChange={e => processFile(e.target.files[0])} />
        {file ? (
          <>
            <FileSpreadsheet size={36} color="#10b981" style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 700, fontSize: 14, color: '#065f46' }}>{file.name}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{importData.length.toLocaleString()} rows detected</div>
            <button onClick={e => { e.stopPropagation(); setFile(null); setImportData([]); setUploadedColumns([]); setColumnMap({}); setMappingTypes({}); }}
              style={{ marginTop: 10, fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Remove file
            </button>
          </>
        ) : (
          <>
            <Upload size={36} color="#94a3b8" style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>Drag & drop or click to upload</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Supports .xls and .xlsx</div>
          </>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Options */}
      {mode === 'bulk_import' && (
        <div style={{ marginTop: 24, padding: '16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
            <input type="checkbox" checked={autoDistribute} onChange={e => setAutoDistribute(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: '#3b82f6' }} />
            Auto Lead Distribution
            <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>(Distribute leads automatically based on CRM settings)</span>
          </label>
        </div>
      )}
    </div>
  );

  const renderStep1 = () => (
    <div style={{ padding: '24px' }}>
      {/* Mapping Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Map Fields</div>
        <button onClick={handleSaveMapping} disabled={isLoading} style={{ padding: '6px 12px', background: '#3b82f6', color: '#fff', borderRadius: 6, border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 }}>
          {isLoading ? 'Saving...' : 'Save Mapping'}
        </button>
      </div>

      <div style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>Existing Field</div>
        <div style={{ flex: 1 }}>Uploaded Field</div>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {STANDARD_COLUMNS.map(stdCol => {
          const type = mappingTypes[stdCol] || 'file';
          const value = columnMap[stdCol] || '';
          const isOther = type === 'other';

          return (
            <div key={stdCol} style={{ display: 'flex', alignItems: 'flex-start', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>

              {/* Existing Field Side */}
              <div style={{ flex: 1, paddingRight: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                  {stdCol} <span style={{ color: '#ef4444' }}>*</span>
                </div>
                <select
                  disabled
                  style={{ width: '100%', fontSize: 13, padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#64748b', outline: 'none', appearance: 'none' }}
                >
                  <option>{stdCol}</option>
                </select>
              </div>

              {/* Uploaded Field Side */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={!isOther}
                      onChange={() => {
                        setMappingTypes(prev => ({ ...prev, [stdCol]: 'file' }));
                        setColumnMap(prev => { const n = { ...prev }; delete n[stdCol]; return n; });
                      }}
                      style={{ accentColor: '#3b82f6', width: 16, height: 16 }}
                    /> From Imported File
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={isOther}
                      onChange={() => {
                        setMappingTypes(prev => ({ ...prev, [stdCol]: 'other' }));
                        setColumnMap(prev => ({ ...prev, [stdCol]: '__other__:' }));
                      }}
                      style={{ accentColor: '#3b82f6', width: 16, height: 16 }}
                    /> Other
                  </label>
                </div>

                {!isOther ? (
                  <select
                    value={value}
                    onChange={e => setColumnMap(prev => ({ ...prev, [stdCol]: e.target.value }))}
                    style={{ width: '100%', fontSize: 13, padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', outline: 'none' }}
                  >
                    <option value="">select field name</option>
                    {uploadedColumns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder={`Enter custom ${stdCol} value`}
                    value={value.replace('__other__:', '')}
                    onChange={e => setColumnMap(prev => ({ ...prev, [stdCol]: `__other__:${e.target.value}` }))}
                    style={{ width: '100%', fontSize: 13, padding: '10px 12px', borderRadius: 6, border: '1px solid #cbd5e1', outline: 'none' }}
                  />
                )}
              </div>

            </div>
          );
        })}
      </div>

    </div>
  );

  const renderStep2 = () => {
    const mappedCount = Object.values(columnMap).filter(v => v && !v.startsWith('__other__:')).length;
    const preview = importData.slice(0, 5);
    const previewCols = Object.keys(preview[0] || {}).slice(0, 6);
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Mode', value: mode === 'bulk_import' ? 'Bulk Import' : 'Bulk Update' },
            { label: 'File', value: file?.name || '-' },
            { label: 'Total Rows', value: importData.length.toLocaleString() },
            { label: 'Mapped Columns', value: `${mappedCount} / ${STANDARD_COLUMNS.length}` },
            { label: 'Auto Distribute', value: autoDistribute ? 'Yes' : 'No' },
          ].map(item => (
            <div key={item.label} style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', wordBreak: 'break-all' }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10 }}>Data Preview (first 5 rows)</div>
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {previewCols.map(c => (
                  <th key={c} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  {previewCols.map(c => (
                    <td key={c} style={{ padding: '7px 12px', color: '#374151', borderBottom: '1px solid #f1f5f9', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(row[c] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, padding: '12px 16px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
          ✅ Ready to import <strong>{importData.length.toLocaleString()}</strong> records. Click <strong>Import Now</strong> to proceed.
        </div>
      </div>
    );
  };




  const isNewTemplate = selectedTemplateId === 'new';
  const totalSteps = isNewTemplate ? 3 : 2;
  const activeSteps = isNewTemplate
    ? ['Select Mode', 'Upload & Map', 'Review', 'Result']
    : ['Select Mode', 'Review', 'Result'];

  const canProceed = () => {
    if (step === 0) {
      if (isNewTemplate && !templateName.trim()) return false;
      if (isNewTemplate) return file && uploadedColumns.length > 0 && !error;
      return file && importData.length > 0 && !error;
    }
    return true;
  };

  const nextStep = () => {
    if (step === totalSteps - 1) { handleSubmit(); return; }
    setStep(s => s + 1);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText="OK"
        onConfirm={alertConfig.onConfirm}
      />
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}>
        <div style={{ width: '92%', maxWidth: 860, background: 'white', borderRadius: 18, boxShadow: '0 25px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>

          {/* Header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#1e293b' }}>Import Enquiry Data</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Bulk import or update enquiry records from Excel</div>
            </div>
            <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <X size={18} color="#64748b" />
            </button>
          </div>

          {/* Step Indicator */}
          {step < 3 && (
            <div style={{ padding: '16px 24px 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {activeSteps.slice(0, totalSteps).map((label, idx) => (
                  <React.Fragment key={label}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 13,
                        background: idx < step ? '#10b981' : idx === step ? '#3b82f6' : '#e2e8f0',
                        color: idx <= step ? 'white' : '#94a3b8',
                        transition: 'all .3s'
                      }}>{idx < step ? <CheckCircle size={14} /> : idx + 1}</div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: idx === step ? '#3b82f6' : idx < step ? '#10b981' : '#94a3b8' }}>{label}</span>
                    </div>
                    {idx < totalSteps - 1 && <div style={{ flex: 1, height: 2, background: idx < step ? '#10b981' : '#e2e8f0', margin: '0 8px', marginBottom: 20, transition: 'background .3s' }} />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Step Content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {step === 0 && renderStep0()}
            {step === 1 && (isNewTemplate ? renderStep1() : renderStep2())}
            {step === 2 && isNewTemplate && renderStep2()}
          </div>

          {/* Footer */}
          {step < 3 && (
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: '#fafafa', borderRadius: '0 0 18px 18px' }}>
              <button
                onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                <ChevronLeft size={16} /> {step === 0 ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed() || isLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '9px 24px', borderRadius: 8, border: 'none',
                  background: canProceed() && !isLoading ? '#3b82f6' : '#cbd5e1',
                  color: 'white', fontWeight: 700, fontSize: 13, cursor: canProceed() && !isLoading ? 'pointer' : 'not-allowed',
                  transition: 'all .2s'
                }}
              >
                {isLoading ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Importing...</>
                  : step === totalSteps - 1 ? <><CheckCircle size={15} /> Import Now</>
                    : <>Next <ChevronRight size={16} /></>}
              </button>
            </div>
          )}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
}
